import { useCallback, useEffect, useMemo, useRef, type MouseEvent } from 'react'
import { useParams } from 'react-router-dom'

import { kActiveCitationHighlightClass, kArtifactReferenceMarkerIdAttr } from '@/agent-v2/artifact-editor/utils/artifact-citation-helpers'
import {
  AgentV2ArtifactCitationTargetKind,
  replaceAgentV2ReadOnlyArtifactCitations,
  type AgentV2ArtifactCitationViewerTarget,
} from '@/agent-v2/artifact-editor/utils/read-only-artifact-citations'
import { useOpenAgentV2ReferenceViewer } from '@/agent-v2/hooks/useOpenAgentV2ReferenceViewer'
import { getAgentV2ReferenceDisplayName } from '@/agent-v2/utils/agent-v2-reference-displays'
import { buildAgentV2ReferenceViewerSession } from '@/agent-v2/references/build-agent-v2-reference-viewer-session'
import { getAgentV2ExternalReferenceUrl } from '@/agent-v2/references/agent-v2-reference-opening'
import { selectAgentV2ReferencesByCitationId } from '@/agent-v2/references/store/selectors'
import { getMemberDisplayName } from '@/collaboration/utils/members'
import { useSourceOpensInChatPanel } from '@/drafting/workspace/hooks/useSourceOpensInChatPanel'
import { useDraftActiveCitationId } from '@/drafting/workspace/hooks/useDraftActiveCitationId'
import { useSyncDraftFootnoteNumbers } from '@/drafting/workspace/hooks/useSyncDraftFootnoteNumbers'
import HTMLBaseRenderer from '@/content-viewer/components/base-renderers/HTMLBaseRenderer'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import ReadOnlyMatterArtifactBanner from '@/matter-artifacts/components/ReadOnlyMatterArtifactBanner'
import { useMatterArtifactViewableMarkdown } from '@/matter-artifacts/hooks/useMatterArtifactViewableMarkdown'
import { selectMatterArtifactVersionById } from '@/matter-artifacts/store/selectors'
import { selectMatterMembers } from '@/matter/store/selectors'
import { resolveStandaloneViewerUrlFromSession } from '@/standalone-reference-viewer/utils/resolve-standalone-viewer-url'
import type { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'

const kActiveCitationHighlightClasses = kActiveCitationHighlightClass.split(' ')

type ReadOnlyMatterArtifactProps = {
  conversationId: string
  matterArtifactVersionId: string
  openSourcesInChatPanel?: boolean
  showOwnerBanner?: boolean
}

/** Read-only view of a matter artifact version for collaborators who don't own it. */
export default function ReadOnlyMatterArtifact({
  conversationId,
  matterArtifactVersionId,
  openSourcesInChatPanel = true,
  showOwnerBanner = true,
}: ReadOnlyMatterArtifactProps) {
  const { matterId } = useParams<{ matterId: string }>()
  const artifactContentRef = useRef<HTMLDivElement>(null)
  const sourceOpensInChatPanel = useSourceOpensInChatPanel() && openSourcesInChatPanel
  const openReferenceViewer = useOpenAgentV2ReferenceViewer()
  const referencesByCitationId = useAppSelector((state: RootState) => selectAgentV2ReferencesByCitationId(state, { conversationId }))
  const activeCitationId = useDraftActiveCitationId()
  const matterArtifactVersion = useAppSelector((state: RootState) => selectMatterArtifactVersionById(state, matterArtifactVersionId))
  const matterMembers = useAppSelector((state: RootState) => selectMatterMembers(state, matterId))
  const ownerDisplayName = useMemo(() => {
    const ownerId = matterArtifactVersion?.created_by_user_id
    if (!ownerId) return null
    const ownerMember = matterMembers.find((member) => member.user_id === ownerId)
    return ownerMember ? getMemberDisplayName(ownerMember) : null
  }, [matterArtifactVersion?.created_by_user_id, matterMembers])
  const { markdownString, htmlString, isLoading, hasError, retry } = useMatterArtifactViewableMarkdown({ matterArtifactVersionId })
  useSyncDraftFootnoteNumbers({ markdown: markdownString, referencesByCitationId })
  const viewerTargetsByCitationId = useMemo(() => {
    const targets: Partial<Record<string, AgentV2ArtifactCitationViewerTarget>> = {}

    for (const [citationId, reference] of Object.entries(referencesByCitationId)) {
      const externalUrl = getAgentV2ExternalReferenceUrl(reference)
      if (externalUrl) {
        targets[citationId] = {
          kind: AgentV2ArtifactCitationTargetKind.WEB,
          displayName: getAgentV2ReferenceDisplayName(reference),
          viewerUrl: externalUrl,
        }
        continue
      }
      const session = buildAgentV2ReferenceViewerSession(reference)
      const viewerUrl = resolveStandaloneViewerUrlFromSession(session, {
        conversationId,
        messageId: reference.turn_id,
        matterId,
      })
      if (!session || !viewerUrl) continue

      targets[citationId] = {
        kind: AgentV2ArtifactCitationTargetKind.VIEWER,
        displayName: getAgentV2ReferenceDisplayName(reference),
        viewerUrl,
      }
    }

    return targets
  }, [conversationId, matterId, referencesByCitationId])
  const transformArtifactReferenceHtml = useCallback(
    (sanitizedHtml: string) =>
      replaceAgentV2ReadOnlyArtifactCitations({
        html: sanitizedHtml,
        markdown: markdownString ?? '',
        referencesByCitationId,
        viewerTargetsByCitationId,
      }),
    [markdownString, referencesByCitationId, viewerTargetsByCitationId]
  )

  // Applied to the links directly because they live in rendered HTML rather than React elements this
  // owns, and re-applied whenever that HTML is rebuilt, which replaces them.
  useEffect(() => {
    const citationLinks = artifactContentRef.current?.querySelectorAll(`a[${kArtifactReferenceMarkerIdAttr}]`)

    citationLinks?.forEach((citationLink) => {
      const isActive = citationLink.getAttribute(kArtifactReferenceMarkerIdAttr) === activeCitationId
      citationLink.classList[isActive ? 'add' : 'remove'](...kActiveCitationHighlightClasses)
    })
  }, [activeCitationId, htmlString, transformArtifactReferenceHtml])

  // In the draft workspace a plain click on a citation opens the source in the chat panel's source tab.
  // Everywhere else, and for a cmd-click or anything else the browser would open in a new tab,
  // the citation stays an ordinary link to the standalone viewer.
  const handleCitationClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!sourceOpensInChatPanel || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
      if (!(event.target instanceof Element)) return

      const citationId = event.target.closest(`a[${kArtifactReferenceMarkerIdAttr}]`)?.getAttribute(kArtifactReferenceMarkerIdAttr)
      const reference = citationId ? referencesByCitationId[citationId] : undefined
      // External sources keep their ordinary link, including in the draft workspace.
      if (!reference || getAgentV2ExternalReferenceUrl(reference) || !openReferenceViewer(reference)) return

      event.preventDefault()
    },
    [openReferenceViewer, referencesByCitationId, sourceOpensInChatPanel]
  )

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      {showOwnerBanner && <ReadOnlyMatterArtifactBanner ownerDisplayName={ownerDisplayName} />}
      {/* Delegated because the citations are links inside rendered HTML, not React elements this owns. */}
      <div ref={artifactContentRef} className="flex-1 w-full overflow-y-auto pb-4" onClick={handleCitationClick}>
        {isLoading ? <ContentViewerLoadingSpinner size="large" /> : null}
        {!isLoading && (hasError || !htmlString) ? <ContentViewerErrorState message="Failed to load markdown content." onRetry={retry} /> : null}
        {!isLoading && !hasError && htmlString ? (
          <HTMLBaseRenderer htmlContent={htmlString} transformArtifactReferenceHtml={transformArtifactReferenceHtml} />
        ) : null}
      </div>
    </div>
  )
}
